import React, { useEffect, useState } from 'react';
import {
  canNativeSharePdf,
  canSharePdfFiles,
  downloadPdfBlob,
  nativeSharePdf,
  openPdfInNativeViewer,
} from '../utils/mobilePdfShare';

/**
 * Mobile PDF action sheet (no iframe preview).
 * Opens the system PDF viewer (correct on iOS Safari/Chrome) and offers Share/Save.
 *
 * @param {object} props
 * @param {Blob} props.blob
 * @param {string} props.filename
 * @param {string} [props.targetLabel]
 * @param {() => void} props.onClose
 * @param {boolean} [props.autoOpenNative=true]
 */
export default function MobilePdfShareOverlay({
  blob,
  filename,
  targetLabel = '',
  onClose,
  autoOpenNative = true,
}) {
  const [shareBusy, setShareBusy] = useState(false);
  const [shareHint, setShareHint] = useState('');
  const [openedNative, setOpenedNative] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (!autoOpenNative || !(blob instanceof Blob) || openedNative) return undefined;
    // Defer so the sheet paints first; native tab uses the system PDF renderer.
    const id = window.setTimeout(() => {
      openPdfInNativeViewer(blob);
      setOpenedNative(true);
    }, 50);
    return () => window.clearTimeout(id);
  }, [autoOpenNative, blob, openedNative]);

  const shareAvailable = canNativeSharePdf(blob, filename);
  const filesShareReady = canSharePdfFiles(blob, filename);

  const handleShare = async () => {
    if (shareBusy) return;
    setShareBusy(true);
    setShareHint('');
    try {
      const result = await nativeSharePdf({
        blob,
        filename,
        title: 'CyberTracer OSINT report',
        text: targetLabel
          ? `CyberTracer report for ${targetLabel}`
          : 'CyberTracer investigation PDF',
        autoSaveOnFileFail: true,
      });
      if (result === 'unsupported') {
        setShareHint('Share is not available on this device. Use Save or Open.');
      } else if (result === 'error') {
        setShareHint('Share failed. Try Save or Open instead.');
      } else if (result === 'saved_fallback') {
        setShareHint('PDF saved. Attach it from Files/Downloads in WhatsApp or Mail.');
      }
    } finally {
      setShareBusy(false);
    }
  };

  if (!(blob instanceof Blob)) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col justify-end bg-slate-950/70"
      role="dialog"
      aria-modal="true"
      aria-label="PDF ready — open or share"
      data-testid="mobile-pdf-share-overlay"
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-2xl border border-slate-700 bg-slate-900 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-600" aria-hidden />
        <p className="truncate text-sm font-semibold text-slate-100">
          {filename || 'CyberTracer-report.pdf'}
        </p>
        <p className="mt-1 text-[11px] leading-snug text-slate-400">
          Report opened in the system PDF viewer. Use Share for WhatsApp or Mail, or Open again if the tab was blocked.
        </p>

        {shareHint ? (
          <p className="mt-2 rounded-lg bg-amber-950/40 px-2.5 py-1.5 text-center text-[11px] text-amber-200" role="status">
            {shareHint}
          </p>
        ) : null}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => {
              openPdfInNativeViewer(blob);
              setOpenedNative(true);
            }}
            className="rounded-xl border border-slate-600 bg-slate-800 px-3 py-3 text-[11px] font-black uppercase tracking-widest text-slate-100"
            data-testid="mobile-pdf-open"
          >
            Open PDF
          </button>
          <button
            type="button"
            onClick={() => downloadPdfBlob(blob, filename)}
            className="rounded-xl border border-slate-600 bg-slate-800 px-3 py-3 text-[11px] font-black uppercase tracking-widest text-slate-100"
            data-testid="mobile-pdf-download"
          >
            Save
          </button>
          <button
            type="button"
            onClick={handleShare}
            disabled={shareBusy || !shareAvailable}
            className="rounded-xl border border-cyan-500/50 bg-cyan-950/50 px-3 py-3 text-[11px] font-black uppercase tracking-widest text-cyan-100 disabled:cursor-not-allowed disabled:opacity-40"
            title={
              shareAvailable
                ? filesShareReady
                  ? 'Share via WhatsApp, Mail, and other apps'
                  : 'Share — if file attach is blocked, PDF is saved first'
                : 'Native share not supported — use Save'
            }
            data-testid="mobile-pdf-share"
          >
            {shareBusy ? '…' : 'Share'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-600 px-3 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-300"
            data-testid="mobile-pdf-close"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
